import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { FiPlus, FiEdit2 } from 'react-icons/fi';
import PropTypes from 'prop-types';
import { createCalculationItem, updateCalculationItem } from '../../../store/calculations';
import CalculationItemForm from './CalculationItemForm';
import CalculationItemModal from './CalculationItemModal';
import { useTranslation } from 'react-i18next';

const CalculationItemManager = ({ 
  calculationId, 
  item = null, 
  onSuccess, 
  buttonText = null, 
  buttonVariant = 'primary',
  children,
  className = '',
  buttonClassName = '',
  iconOnly = false,
  disabled = false
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleOpenModal = useCallback((e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setError(null);
  }, []);

  const handleSubmit = useCallback(async (itemData) => {
    try {
      setIsSubmitting(true);
      setError(null);

      if (item) {
        // Update existing item - pass calculationId so saga can refresh
        dispatch(updateCalculationItem(item.id, itemData, calculationId));
      } else {
        // Create new item
        dispatch(createCalculationItem(calculationId, itemData));
      }

      setIsSubmitting(false);
      handleCloseModal();
      
      // Call onSuccess callback if provided
      // The saga will handle refreshing the calculation data
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error saving calculation item:', err);
      setError({ 
        message: err.response?.data?.message || err.message || t('calculation.failedToSave') 
      });
      setIsSubmitting(false);
    }
  }, [dispatch, item, calculationId, handleCloseModal, onSuccess, t]);

  const getButtonClasses = useCallback(() => {
    const baseClasses = `inline-flex items-center justify-center rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${buttonClassName}`;
    
    if (iconOnly) {
      return `${baseClasses} p-1.5`;
    }
    
    switch (buttonVariant) {
      case 'primary':
        return `${baseClasses} px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`;
      case 'secondary':
        return `${baseClasses} px-4 py-2 text-sm text-blue-700 bg-blue-100 hover:bg-blue-200 focus:ring-blue-500 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`;
      case 'danger':
        return `${baseClasses} px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 focus:ring-red-500 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`;
      case 'ghost':
      default:
        return `${baseClasses} p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 focus:ring-blue-500 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`;
    }
  }, [buttonVariant, buttonClassName, iconOnly, disabled]);

  const renderButtonContent = useCallback(() => {
    if (children) return children;
    
    const icon = item ? (
      <FiEdit2 className={iconOnly ? 'h-4 w-4' : '-ml-0.5 mr-2 h-4 w-4'} />
    ) : (
      <FiPlus className={iconOnly ? 'h-4 w-4' : '-ml-0.5 mr-2 h-4 w-4'} />
    );
    
    if (iconOnly) {
      return icon;
    }
    
    return (
      <>
        {icon}
        {buttonText || (item ? t('common.edit') : t('calculation.addItem'))}
      </>
    );
  }, [children, item, buttonText, iconOnly, t]);

  return (
    <div className={`inline-block ${className}`}>
      <button
        type="button"
        onClick={handleOpenModal}
        disabled={disabled}
        className={getButtonClasses()}
        aria-label={item ? t('calculation.editItem') : t('calculation.addItem')}
        title={iconOnly ? (item ? t('calculation.editItem') : t('calculation.addItem')) : undefined}
      >
        {renderButtonContent()}
      </button>

      <CalculationItemModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={item ? t('calculation.editItem') : t('calculation.addItem')}
        size="lg"
      >
        <CalculationItemForm
          item={item}
          calculationId={calculationId}
          onSave={handleSubmit}
          onCancel={handleCloseModal}
          loading={isSubmitting}
          error={error}
        />
      </CalculationItemModal>
    </div>
  );
};

CalculationItemManager.propTypes = {
  calculationId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    productCode: PropTypes.string,
    productDescription: PropTypes.string,
    quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    listPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    buyPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    stockPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sellPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    customsValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    customsAvailability: PropTypes.bool,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string
  }),
  onSuccess: PropTypes.func,
  buttonText: PropTypes.string,
  buttonVariant: PropTypes.oneOf(['primary', 'secondary', 'danger', 'ghost']),
  children: PropTypes.node,
  className: PropTypes.string,
  buttonClassName: PropTypes.string,
  iconOnly: PropTypes.bool,
  disabled: PropTypes.bool
};

export default CalculationItemManager;
